import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ListItemButton } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useCompanies } from '../../../hooks/useCompanies';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import CompanyForm from '../forms/CompanyForm';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import Box from '@mui/material/Box';
import { useDeleteCompany } from '../../../hooks/useDeleteCompany';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { getTierLabel } from '../../../utils/getTierlabel';

export default function Companies(props) {
    
    const { t } = useTranslation('common'); 

    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };   

    const { companies, loading, setCompanies } = useCompanies(open);

    const { remove } = useDeleteCompany(setCompanies);

    const [formOpen, setFormOpen] = React.useState(false);
    const [selectedCompany, setSelectedCompany] = React.useState(null);
    const [formMode, setFormMode] = React.useState("create");

    const { showSnackbar } = useSnackbar();

    const handleAdd = () => {
        setSelectedCompany(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (company) => {
        setSelectedCompany(company);
        setFormMode("edit");
        setFormOpen(true);
    };

    const handleDelete = async (company) => {
        try {
            await remove(company.id);
            showSnackbar(t('companies.messages.delete_success', { name: company.name }), "success");
        } catch (err) {
            showSnackbar(t('companies.messages.delete_fail', { name: company.name }), "error");
        }
    };

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    return(
        <React.Fragment>
            <ListItem>
                <ListItemButton onClick={handleClickOpen} >
                    <ListItemIcon>
                        <BusinessIcon fontSize='large' />
                    </ListItemIcon>

                    <ListItemText 
                        primary="Companies"
                        secondary="view, add, edit, delete companies"
                    >
                    </ListItemText>
                </ListItemButton>
            </ListItem>

            <Dialog
                onClose={handleClose}
                open={open}
                disableRestoreFocus
            >
                <DialogTitle>
                    Companies
                </DialogTitle>
            
                <DialogContent dividers>
                    <List>
                        {companies
                        ?.slice() // create a shallow copy so we don’t mutate state directly
                        .sort((a, b) => a.name.localeCompare(b.name)) // sort alphabetically
                        .map((company) => (
                        <ListItem
                            key={company.id}
                            secondaryAction={
                                <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                                    <VerticalHamburgerMenu 
                                        options={[
                                        {
                                            id: 1,
                                            name: 'Edit',
                                            callback: () => handleEdit(company),
                                        },
                                        ...(company.offices?.length === 0
                                        ? 
                                        [
                                            {
                                                id: 2,
                                                name: 'Delete',
                                                callback: () => handleDelete(company),
                                                confirmMessage: t('companies.messages.delete_confirm', { name: company.name }),
                                            },
                                        ]
                                        : []),
                                    ]}

                                    />
                                </Box>
                            }
                        >
                            <ListItemText 
                                primary={`${company.name}`}  
                                secondary={`tier: ${getTierLabel(company.tier)}, ${company.offices ? `offices: ${company.offices.length}` : null}`}
                            />
                        </ListItem>
                        ))}
                    </List>

                    <CompanyForm 
                        setCompanies={setCompanies} 
                        company={selectedCompany}
                        mode={formMode}
                        open={formOpen}
                        onClose={() => setFormOpen(false)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAdd}>Add Company</Button>
                    <div style={{flex: '1 0 0'}} />
                    <Button onClick={handleClose}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}